import React, { useState, useEffect } from "react";
import { Plus, Landmark, TrendingUp, TrendingDown, Users, Banknote, ChevronRight } from "lucide-react";
import { api } from "../../utils/api";
import PageHead from "../../components/PageHead";
import DashboardCharts from "../../components/DashboardCharts"; 
import Balances from "./Balances";
const money = n => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n || 0);
const fmt = d => d ? new Date(d).toLocaleDateString("tr-TR") : "—";

export default function Dashboard({user, onNavigate}){ 
  const [data,setData]=useState(null);
  
  useEffect(()=>{
    api.get("/dashboard").then(r=>setData(r.data))
  },[]);
  
  if(!data)return <div className="loading">Veriler hazırlanıyor...</div>;
  
  const ap=data.active_period;
  const today=new Date();
  const todayLabel=today.toLocaleDateString("tr-TR",{day:"2-digit",month:"long",year:"numeric"}).toUpperCase();
  const hr = today.getHours();
  const greet = (hr >= 6 && hr < 12) ? "Günaydın" : (hr >= 12 && hr < 18) ? "İyi günler" : (hr >= 18 && hr < 22) ? "İyi akşamlar" : "İyi geceler";
  const firstName=((user?.name||"").trim().split(" ")[0])||((user?.email||"").split("@")[0])||"Yönetici";
  
  let progress=0;
  let dateRange="";
  if(ap?.start_date&&ap?.end_date){
    const s=new Date(ap.start_date),e=new Date(ap.end_date);
    const total=e-s,done=Math.max(0,Math.min(total,today-s));
    progress=total>0?Math.round(done*100/total):0;
    dateRange=`${s.toLocaleDateString("tr-TR")} — ${e.toLocaleDateString("tr-TR")}`
  }

  const handleNav = (hedef) => {
    if(onNavigate) onNavigate(hedef);
    else window.location.href = `/${hedef}`;
  };
  
  return <>
    <PageHead eyebrow={`GENEL BAKIŞ · ${todayLabel}`} title={`${greet}, ${firstName}.`} sub="Site'nin finansal durumuna hızlıca göz atın." action={<button className="primary" onClick={()=>handleNav("transactions")}><Plus size={17}/> Yeni işlem</button>}/>
    
    <section className="hero-strip">
      <div>
        <span className="eyebrow light">AKTİF DÖNEM</span>
        <h2>{ap?.name||"Aktif dönem tanımlanmadı"}</h2>
        <p>{ap?dateRange:"Dönemler sayfasından bir dönem aktifleştirin"} {ap&&<span className="period-pill">% {progress} tamamlandı</span>}</p>
      </div>
      <div className="hero-meta">
        <div><small>Toplam villa</small><strong>{data.owner_count} <em>/ 91</em></strong></div>
        <div><small>Dönem aidatı</small><strong>{ap?money(ap.donem_aidat_toplami||((ap.due_1||0)*(ap.due_1_months||0)+(ap.due_2||0)*(ap.due_2_months||0))):"—"}</strong></div>
      </div>
    </section>
    
    <div className="metric-grid">
      <Metric icon={Landmark} label="Toplam bakiye" value={money(data.balance)} note="Banka + kasa net" tone="green"/>
      <Metric icon={TrendingUp} label="Toplam gelir" value={money(data.income)} note="Bu dönem" tone="gold"/>
      <Metric icon={TrendingDown} label="Toplam gider" value={money(data.expense)} note="Bu dönem" tone="rose"/>
      <Metric icon={Users} label="Toplam Aidat Alacakları" value={money(data.pending_dues || 0)} note={`${data.debtor_count || 0} borçludan net alacak`} tone="slate"/>
    </div>
    
    {/* YENİ GRAFİKLERİMİZ BURADA */}
    <DashboardCharts onNavigate={handleNav} />
    
    {/* KAYBOLAN TABLOLAR (AŞAĞIDAKİ KISIM) */}
    <div className="dashboard-grid">
      <section className="panel account-panel">
        <div className="panel-head">
          <div><span className="eyebrow">NAKİT DURUMU</span><h3>Hesaplar</h3></div>
          <button className="text-button" onClick={()=>handleNav("balances")}>Detaylar <ChevronRight size={15}/></button>
        </div>
        {/* Nakit Kasa her zaman en üstte gösterilir */}
        
        
       {/* API'den gelen sadece 3 hesabı temizce listele */}
        {Object.entries(data?.accounts || {}).map(([bankName, amount], index) => (
          <Account 
            key={index} 
            label={bankName === "Nakit" ? "Nakit Kasa" : bankName} 
            icon={bankName === "Nakit Kasa" || bankName === "Nakit" ? Banknote : Landmark} 
            amount={amount || 0}
          />
        ))}
      </section>
      
      <section className="panel">
        <div className="panel-head">
          <div><span className="eyebrow">SON HAREKETLER</span><h3>İşlem akışı</h3></div>
          <button className="text-button" onClick={()=>handleNav("transactions")}>Tümünü gör <ChevronRight size={15}/></button>
        </div>
        {data?.transactions?.length ? data.transactions.map((x,i)=><div className="activity" key={i}><div className={`activity-icon ${x.kind}`}><span>{x.kind==="income"?"+":"−"}</span></div><div><strong>{x.description}</strong><small>{x.category} · {fmt(x.date)}</small></div><b className={x.kind}>{x.kind==="income"?"+":"−"}{money(x.amount)}</b></div>) : <div className="empty">Henüz işlem bulunmuyor.</div>}
      </section>
    </div>
  </>
}

function Metric({icon:Icon, label, value, note, tone}) {
  return (
    <div className={`metric ${tone}`} data-testid={`metric-${tone}`}>
      <div className="metric-icon"><Icon size={18}/></div>
      <small>{label}</small>
      <strong>{value}</strong>
      <span>{note}</span>
    </div>
  );
}

function Account({label, icon:Icon, amount}) {
  return (
    <div className="account-row">
      <div className="bank-icon"><Icon size={17}/></div>
      <div>
        <strong>{label}</strong>
        <small>Güncel bakiye</small>
      </div>
      <b>{money(amount)}</b>
    </div>
  );
}